import type { ReactNode } from 'react';
import { demoEnabled } from '../lib/auth';
import { Logo } from '../components/Logo';

import { TRACKED_REG } from '../lib/flightTrack';
/** A made-up registration. The demo must never point at a real airplane. */

export function SplashAuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 grid lg:grid-cols-2">
        <div
          className="relative hidden overflow-hidden lg:flex"
          style={{ background: 'linear-gradient(140deg, #eef4fc 0%, #dfeafb 45%, #cdddf6 100%)' }}
        >
          {/* soft glows */}
          <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-sky-300/40 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-16 h-[28rem] w-[28rem] rounded-full bg-brand/10 blur-3xl" />

          {/* faint flight-path arcs */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
            viewBox="0 0 400 600"
            preserveAspectRatio="xMidYMid slice"
            fill="none"
          >
            <path d="M-20 470 C 120 350, 260 420, 440 230" stroke="url(#fp)" strokeWidth="1.5" strokeDasharray="2 7" />
            <path d="M-20 560 C 160 500, 300 540, 440 370" stroke="url(#fp)" strokeWidth="1" strokeDasharray="2 9" />
            <path d="M-20 190 C 100 130, 240 220, 440 110" stroke="url(#fp)" strokeWidth="1" strokeDasharray="2 9" />
            <defs>
              <linearGradient id="fp" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#2A4D8C" stopOpacity="0" />
                <stop offset="0.5" stopColor="#3a66b5" />
                <stop offset="1" stopColor="#2A4D8C" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>

          {/* oversized watermark of the real logo */}
          <img
            src={`${import.meta.env.BASE_URL}ajm-logo.png`}
            alt=""
            className="pointer-events-none absolute -bottom-10 -right-20 h-56 w-auto opacity-[0.05]"
            draggable={false}
          />

          <div className="relative z-10 flex w-full flex-col justify-between p-12">
            <Logo className="text-navy-900 text-3xl" />

            <div className="max-w-md">
              <span className="mb-4 inline-flex items-center rounded-full border border-navy-900/15 bg-white/60 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.2em] text-navy-700">
                {demoEnabled() ? 'Live demo' : 'Lessee Portal'}
              </span>
              <h2 className="font-display text-[2.6rem] font-bold leading-[1.05] text-navy-900">
                Fly {TRACKED_REG}.
                <br />
                <span className="text-navy-600">Booked, logged, handled.</span>
              </h2>
              <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-ink-muted">
                {demoEnabled()
                  ? 'Reserve the airplane, log your flights, and manage your documents — the whole operation in one place. This is a working demo on a sample aircraft.'
                  : 'Reserve the aircraft, log your flights, and manage your documents — the whole operation in one place, built for AJM Aviation Ventures lessees.'}
              </p>
              <div className="mt-7 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-navy-700/60">
                <span className="h-px w-10 bg-gradient-to-r from-brand/60 to-transparent" />
                {demoEnabled() ? 'Sample aircraft · sample data' : '1961 Cessna 172C · Based at KLGB'}
              </div>
            </div>

            <div className="text-xs text-ink-subtle">
              © {new Date().getFullYear()} AJM Aviation Ventures, LLC
              <span className="mx-1.5">·</span>
              Powered by <span className="font-medium text-navy-700/70">Solari</span>
            </div>
          </div>
        </div>

        <div className="relative flex items-center justify-center overflow-hidden bg-white p-6 sm:p-10">
          {/* Cropped, faded bug — tech-y backdrop behind the form. */}
          <img
            src={`${import.meta.env.BASE_URL}ajm-logo-bug.png`}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-24 h-80 w-auto rotate-6 opacity-[0.06] sm:h-[28rem]"
            draggable={false}
          />
          <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-auto opacity-[0.04]">
            <img
              src={`${import.meta.env.BASE_URL}ajm-logo-bug.png`}
              alt=""
              aria-hidden="true"
              className="h-full w-auto -rotate-12"
              draggable={false}
            />
          </div>

          <div className="relative z-10 w-full max-w-md">
            <div className="lg:hidden mb-8 flex justify-center">
              <Logo className="text-navy-900 text-[2rem]" />
            </div>
            <h1 className="text-[28px] sm:text-[32px] font-display font-bold tracking-tight text-navy-900">
              {title}
            </h1>
            {subtitle && <p className="text-ink-muted mt-1.5 text-[15px]">{subtitle}</p>}
            <div className="mt-7">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
